// Tools → WH Labels: the warehouse's own label maker for the small 35×80 mm
// roll on the second Zebra. Three kinds: stock-section labels (A 12, single or
// range), product BAG labels (WMS picklist number + the product's barcode so a
// bagged item still scans at packing), and plain text shelf labels (picklist
// number alone, or free text like "James Black 2-3XL" for stacked boxes).
//
// Printing rides the exact same path as shipping labels: detectZebraIfEnabled()
// → POST format zpl/pdf → deliverLabel() (direct ZPL to Zebra Browser Print's
// default device, else the mm-exact PDF print popup). Nothing here talks to a
// carrier or a shop — the backend just draws PDFs. (Claude, 2026-09-04)
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch, API } from './api';
import { deliverLabel, detectZebraIfEnabled } from './labelPrint';

interface Product {
  wms_id: number;
  webshop_id?: string;
  article_number?: string;
  picklist_number?: string;
  name?: string;
  size?: string;
  barcode?: string;
  stock_location?: string;
}

interface QueueRow {
  key: number;
  product: Product;
  copies: number;
  withBarcode: boolean;
}

type Tab = 'sections' | 'products' | 'text';

const inputCls = 'bg-gray-800 border border-gray-700 focus:border-blue-500 text-white rounded-lg px-3 py-2 text-sm focus:outline-none';

/** The picklist value we print — the WMS's own picklist number (Fernando's
 *  rule: "picklist number is ours to control"), article number as fallback. */
const picklistOf = (p: Product) => (p.picklist_number || '').trim() || (p.article_number || '').trim();

export default function WHLabelsPage() {
  const [tab, setTab] = useState<Tab>('sections');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const appRole = localStorage.getItem('wms_role') || 'staff';

  // ── the shared print path ──────────────────────────────────────────────────
  const printLabels = useCallback(async (labels: any[]) => {
    if (busy || labels.length === 0) return;
    setMsg('');
    // popup must be opened synchronously in the click (popup blockers)
    const win = window.open('', '_blank');
    if (win) win.document.write('<p style="font-family:sans-serif;padding:2rem">Preparing labels…</p>');
    setBusy(true);
    try {
      const zebra = await detectZebraIfEnabled();
      const res = await authFetch(`${API}/wh-labels/print`, {
        method: 'POST',
        body: JSON.stringify({ labels, format: zebra ? 'zpl' : 'pdf' }),
      });
      const data = await res.json();
      if (!res.ok) { if (win) win.close(); setMsg(data.detail || 'Print failed'); return; }
      await deliverLabel(win, data, zebra);
    } catch {
      if (win) win.close();
      setMsg('Print failed — check the connection');
    } finally { setBusy(false); }
  }, [busy]);

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold text-white">WH Labels</h1>
      <p className="text-gray-500 text-sm mt-0.5 mb-5">
        Section, product and shelf labels for the small thermal roll. Bag labels carry the
        product's barcode; shelf labels are text only.
      </p>

      <div className="flex gap-1 mb-5">
        {([['sections', 'Stock sections'], ['products', 'Product labels'], ['text', 'Custom text']] as [Tab, string][]).map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); setMsg(''); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              tab === k ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {msg && (
        <div className="mb-4 text-red-400 text-sm bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-2.5">
          {msg}
        </div>
      )}

      {tab === 'sections' && <SectionsTab busy={busy} onPrint={printLabels} />}
      {tab === 'products' && <ProductsTab busy={busy} onPrint={printLabels} />}
      {tab === 'text' && <TextTab busy={busy} onPrint={printLabels} />}

      <SettingsCard canEdit={appRole === 'admin'} />
    </div>
  );
}

// ── Stock sections: A 12, or a whole range A 12…A 40 ─────────────────────────
function SectionsTab({ busy, onPrint }: { busy: boolean; onPrint: (l: any[]) => void }) {
  const [prefix, setPrefix] = useState('A');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [copies, setCopies] = useState(1);
  // ?codes=J95,J96,K5,K4 — a new pallet's sections from the floor plan ("Print the location labels",
  // 2026-10-02): a pallet spans two faces, so it is a list, not one letter's range
  const [preset, setPreset] = useState<string[]>(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('codes') || '';
      return q.split(',').map(c => c.trim().toUpperCase().replace(/\s+/g, ''))
        // a named special place (CAMPAIGN, 3.11) is a word, printed as it is
        .filter(c => /^[A-ZÅÄÖ]{1,3}[0-9]{1,4}$/.test(c) || /^[A-ZÅÄÖ]{3,12}$/.test(c)).slice(0, 60)
        .map(c => c.replace(/^([A-ZÅÄÖ]+)([0-9]+)$/, '$1 $2'));
    } catch { return []; }
  });
  if (preset.length > 0) {
    const n = preset.length * Math.max(1, copies);
    return (
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 space-y-4">
        <div className="text-sm text-gray-300">From the floor plan — the new pallet's sections:
          <div className="text-white font-semibold mt-1">{preset.join(' · ')}</div></div>
        <label className="text-sm text-gray-400 block">Copies each
          <input type="number" min={1} max={50} value={copies}
            onChange={e => setCopies(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            className={`${inputCls} block mt-1 w-20 text-center`} />
        </label>
        <div className="flex gap-3 items-center flex-wrap">
          <button disabled={busy} onClick={() => onPrint(preset.map(text => ({ kind: 'section', text, copies })))}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-bold rounded-lg">
            🖨 Print {n} label{n !== 1 ? 's' : ''}
          </button>
          <button onClick={() => setPreset([])} className="text-sm text-gray-400 underline">Type a range instead</button>
        </div>
      </div>
    );
  }

  const p = prefix.trim().toUpperCase();
  const f = parseInt(from, 10);
  const t = to.trim() === '' ? f : parseInt(to, 10);
  const prefixOk = /^[A-ZÅÄÖ]{1,3}$/.test(p);
  const single = from.trim() === '' && to.trim() === '';   // letter-only label
  const rangeOk = single || (!isNaN(f) && !isNaN(t) && t >= f && t - f < 200);

  const texts: string[] = !prefixOk || !rangeOk ? []
    : single ? [p]
    : Array.from({ length: t - f + 1 }, (_, i) => `${p} ${f + i}`);
  const count = texts.length * Math.max(1, copies);

  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <label className="text-sm text-gray-400">Section letter
          <input value={prefix} onChange={e => setPrefix(e.target.value)} maxLength={3}
            className={`${inputCls} block mt-1 w-20 text-center uppercase`} />
        </label>
        <label className="text-sm text-gray-400">From
          <input value={from} onChange={e => setFrom(e.target.value.replace(/\D/g, ''))}
            placeholder="12" className={`${inputCls} block mt-1 w-24 text-center`} />
        </label>
        <label className="text-sm text-gray-400">To (optional)
          <input value={to} onChange={e => setTo(e.target.value.replace(/\D/g, ''))}
            placeholder="40" className={`${inputCls} block mt-1 w-24 text-center`} />
        </label>
        <label className="text-sm text-gray-400">Copies each
          <input type="number" min={1} max={50} value={copies}
            onChange={e => setCopies(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            className={`${inputCls} block mt-1 w-20 text-center`} />
        </label>
      </div>

      <div className="text-sm text-gray-500 min-h-[1.25rem]">
        {texts.length > 0
          ? <>Will print <span className="text-white font-semibold">{count}</span> label{count !== 1 ? 's' : ''}:{' '}
              <span className="text-gray-300">{texts.slice(0, 6).join(', ')}{texts.length > 6 ? ` … ${texts[texts.length - 1]}` : ''}</span></>
          : 'Type a section letter, and a number or a from–to range (leave numbers empty for a letter-only label).'}
      </div>

      <button disabled={busy || texts.length === 0}
        onClick={() => onPrint(texts.map(text => ({ kind: 'section', text, copies })))}
        className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-bold rounded-lg">
        🖨 Print {count > 0 ? count : ''} label{count !== 1 ? 's' : ''}
      </button>
    </div>
  );
}

// ── Product labels: search wms_products, queue, print ─────────────────────────
function ProductsTab({ busy, onPrint }: { busy: boolean; onPrint: (l: any[]) => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [searching, setSearching] = useState(false);
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const keyRef = useRef(1);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await authFetch(`${API}/products/v2?search=${encodeURIComponent(term)}&per_page=20`);
        const d = await r.json();
        setResults(r.ok ? (d.products || []) : []);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q]);

  const add = (p: Product) => {
    setQueue(qu => [...qu, { key: keyRef.current++, product: p, copies: 1, withBarcode: !!(p.barcode || '').trim() }]);
  };
  const patch = (key: number, up: Partial<QueueRow>) =>
    setQueue(qu => qu.map(r => r.key === key ? { ...r, ...up } : r));

  const totalLabels = queue.reduce((s, r) => s + r.copies, 0);

  const print = () => {
    const labels = queue.map(r => {
      const picklist = picklistOf(r.product);
      // with barcode = a BAG label; without = a plain text shelf label
      return r.withBarcode
        ? { kind: 'product', picklist, barcode: (r.product.barcode || '').trim(), copies: r.copies }
        : { kind: 'text', lines: [picklist], copies: r.copies };
    });
    onPrint(labels);
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search products — name, picklist #, article #, barcode, shelf…"
          className={`${inputCls} w-full`} />
        {searching && <div className="text-gray-500 text-sm mt-3">Searching…</div>}
        {!searching && results.length > 0 && (
          <div className="mt-3 divide-y divide-gray-800 max-h-72 overflow-y-auto">
            {results.map(p => (
              <button key={p.wms_id} onClick={() => add(p)}
                className="w-full text-left py-2 px-2 hover:bg-gray-800 rounded flex items-baseline gap-3">
                <span className="text-white text-sm font-medium">{picklistOf(p) || '(no picklist #)'}</span>
                <span className="text-gray-500 text-xs truncate">{p.name} {p.size ? `· ${p.size}` : ''}</span>
                <span className="ml-auto text-gray-600 text-xs whitespace-nowrap">
                  {p.stock_location || ''}{p.barcode ? '' : ' · no barcode'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {queue.length > 0 && (
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 space-y-2">
          <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-1">Print queue</div>
          {queue.map(r => {
            const hasBarcode = !!(r.product.barcode || '').trim();
            return (
              <div key={r.key} className="flex items-center gap-3 text-sm border border-gray-800 rounded-lg px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium truncate">{picklistOf(r.product)}</div>
                  <div className="text-gray-500 text-xs truncate">{r.product.name} {r.product.size ? `· ${r.product.size}` : ''}</div>
                </div>
                <label className={`flex items-center gap-1.5 text-xs ${hasBarcode ? 'text-gray-300' : 'text-gray-600'}`}
                  title={hasBarcode ? 'Bag label: picklist number + barcode' : 'This product has no barcode — text label only'}>
                  <input type="checkbox" checked={r.withBarcode} disabled={!hasBarcode}
                    onChange={e => patch(r.key, { withBarcode: e.target.checked })}
                    className="accent-blue-500" />
                  barcode
                </label>
                <input type="number" min={1} max={50} value={r.copies}
                  onChange={e => patch(r.key, { copies: Math.max(1, Math.min(50, Number(e.target.value) || 1)) })}
                  className={`${inputCls} w-16 text-center py-1`} />
                <button onClick={() => setQueue(qu => qu.filter(x => x.key !== r.key))}
                  className="text-gray-600 hover:text-red-400">✕</button>
              </div>
            );
          })}
          <div className="flex items-center gap-3 pt-2">
            <button disabled={busy} onClick={print}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-bold rounded-lg">
              🖨 Print {totalLabels} label{totalLabels !== 1 ? 's' : ''}
            </button>
            <button onClick={() => setQueue([])} className="text-gray-500 hover:text-gray-300 text-sm">Clear</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Custom text: up to 3 free lines ──────────────────────────────────────────
function TextTab({ busy, onPrint }: { busy: boolean; onPrint: (l: any[]) => void }) {
  const [lines, setLines] = useState(['', '', '']);
  const [copies, setCopies] = useState(1);
  const filled = lines.map(l => l.trim()).filter(Boolean);

  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 space-y-3">
      <div className="text-gray-500 text-sm">
        Up to 3 lines — e.g. <span className="text-gray-300">James Black</span> / <span className="text-gray-300">2-3XL</span> for a box inside a section.
      </div>
      {lines.map((l, i) => (
        <input key={i} value={l} maxLength={60}
          onChange={e => setLines(ls => ls.map((x, j) => j === i ? e.target.value : x))}
          placeholder={i === 0 ? 'Line 1 (largest)' : `Line ${i + 1} (optional)`}
          className={`${inputCls} w-full max-w-md block`} />
      ))}
      <div className="flex items-center gap-3 pt-1">
        <label className="text-sm text-gray-400">Copies
          <input type="number" min={1} max={50} value={copies}
            onChange={e => setCopies(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            className={`${inputCls} block mt-1 w-20 text-center`} />
        </label>
      </div>
      <button disabled={busy || filled.length === 0}
        onClick={() => onPrint([{ kind: 'text', lines: filled, copies }])}
        className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-bold rounded-lg">
        🖨 Print {copies} label{copies !== 1 ? 's' : ''}
      </button>
    </div>
  );
}

// ── Label stock settings (admin edits; everyone sees what's loaded) ──────────
function SettingsCard({ canEdit }: { canEdit: boolean }) {
  const [st, setSt] = useState<{ width_mm: number; height_mm: number; margin_mm: number; section_barcode?: boolean } | null>(null);
  const [saved, setSaved] = useState('');

  useEffect(() => {
    authFetch(`${API}/wh-labels/settings`).then(async r => { if (r.ok) setSt(await r.json()); }).catch(() => {});
  }, []);

  if (!st) return null;

  const save = async () => {
    setSaved('');
    const r = await authFetch(`${API}/wh-labels/settings`, { method: 'PUT', body: JSON.stringify(st) });
    const d = await r.json().catch(() => ({} as any));
    setSaved(r.ok ? 'Saved.' : (d.detail || 'Save failed'));
  };

  return (
    <div className="mt-6 bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
      <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2">Label stock</div>
      <div className="flex items-end gap-3 flex-wrap">
        {(['width_mm', 'height_mm', 'margin_mm'] as const).map(k => (
          <label key={k} className="text-sm text-gray-400">
            {k === 'width_mm' ? 'Width (mm)' : k === 'height_mm' ? 'Height (mm)' : 'Margin (mm)'}
            <input type="number" step="0.5" value={st[k]} disabled={!canEdit}
              onChange={e => setSt(s => s ? { ...s, [k]: Number(e.target.value) } : s)}
              className={`${inputCls} block mt-1 w-24 text-center disabled:opacity-50`} />
          </label>
        ))}
        {canEdit && (
          <button onClick={save} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">Save</button>
        )}
        {saved && <span className="text-sm text-gray-400 pb-2">{saved}</span>}
      </div>
      <p className="text-gray-600 text-xs mt-2">
        Width is across the roll. Text automatically runs along the label's long edge —
        if a test print comes out rotated, swap width and height here.
      </p>
      {/* inbounds (29.10): the section code as a barcode under the text, scanned at the shelf to confirm a replenish stop */}
      <label className="flex items-center gap-2 text-sm text-gray-400 mt-3">
        <input type="checkbox" checked={!!st.section_barcode} disabled={!canEdit}
          onChange={e => setSt(s => s ? { ...s, section_barcode: e.target.checked } : s)} />
        Section labels carry a barcode of the code (for scanning the shelf when replenishing inbounds)
      </label>
    </div>
  );
}
